import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Programmatic Advertising with Precision and Control",
  seoDescription: "Automated media buying that combines the efficiency of machines with the intelligence of strategy. Quality reach with brand safety and performance accountability.",
  canonicalUrl: "https://xperfomax.com/programmatic",
  category: "services",
  categoryLabel: "Services / Programmatic",
  headline: "Reach at scale with precision and control.",
  subheadline: "Automated media buying that combines the efficiency of machines with the intelligence of strategy.",
  heroSecondaryText: "We build programmatic programs that deliver quality reach while maintaining brand safety and performance accountability.",
  ctaText: "Start a serious conversation",
  problemTitle: "Why most companies struggle with programmatic.",
  problems: [
    "Opaque supply chain hides where ads actually run.",
    "Brand safety risks place ads adjacent to harmful content.",
    "Budgets flow to low-value placements without visibility.",
    "Fragmented measurement cannot connect activity to outcomes.",
  ],
  systemTitle: "Our Programmatic System",
  systemDescription: "Audience intelligence, inventory quality, performance optimization, and measurement integration. Every element designed for quality reach.",
  services: [
    { name: "Audience Modeling and Activation", icon: "Users", components: ["CRM data activation", "Behavioral targeting", "Third-party signal integration"], outcome: "Custom audience segments for precise targeting at scale." },
    { name: "Inventory Quality Management", icon: "Shield", components: ["Supply path optimization", "Brand safety controls", "Viewability standards"], outcome: "Ads run in appropriate, viewable environments." },
    { name: "Cross-Channel Coordination", icon: "Layers", components: ["Display and video management", "Native and CTV integration", "Audio channel expansion"], outcome: "Unified programmatic strategy across all channels." },
    { name: "Dynamic Creative Optimization", icon: "Zap", components: ["Personalized creative variations", "Audience-based messaging", "Performance-driven optimization"], outcome: "Right message to right audience at right time." },
    { name: "Attribution and Measurement", icon: "Target", components: ["Multi-touch attribution", "Cross-device tracking", "Business outcome connection"], outcome: "Programmatic activity tied to downstream conversions." },
    { name: "Fraud Prevention and Viewability", icon: "Eye", components: ["Verification partnerships", "Quality impression optimization", "Wasted spend minimization"], outcome: "Maximum quality impressions, minimum waste." },
  ],
  systemShift: {
    title: "The programmatic shift.",
    beforeTitle: "Before: Opaque Volume Buying",
    afterTitle: "After: Transparent Quality Reach",
    items: [
      { label: "Supply Chain", before: "Opaque inventory with unknown ad placements", after: "Transparent supply path with verified environments" },
      { label: "Brand Safety", before: "Ads adjacent to harmful or irrelevant content", after: "Multi-layer protection with ongoing monitoring" },
      { label: "Audience Precision", before: "Broad segments with limited relevance", after: "Custom models from first-party and behavioral data" },
      { label: "Measurement", before: "Impression metrics disconnected from outcomes", after: "Attribution connecting activity to business results" },
      { label: "Quality Standards", before: "Budget flowing to low-value placements", after: "Viewability and fraud controls maximizing quality" },
    ],
  },
  engagementShape: {
    title: "How programmatic engagement unfolds.",
    description: "No timelines. No deliverables lists. Structure that adapts to your inventory and audience requirements.",
  },
  processTitle: "From audit to scale.",
  processSteps: [
    { number: "01", title: "Audit and Assessment", description: "Evaluating current programmatic activity, data assets, and technology stack to identify opportunities and gaps." },
    { number: "02", title: "Audience Strategy", description: "Developing audience models from first-party data, third-party segments, and contextual signals aligned to business objectives." },
    { number: "03", title: "Inventory and Safety Planning", description: "Selecting inventory sources, establishing brand safety protocols, and configuring supply path optimization." },
    { number: "04", title: "Campaign Activation", description: "Launching campaigns with proper creative trafficking, frequency management, and real-time bidding optimization." },
    { number: "05", title: "Measurement and Optimization", description: "Continuous analysis of performance data, viewability metrics, and conversion attribution to refine targeting and allocation." },
    { number: "06", title: "Scale and Integration", description: "Expanding reach across channels and formats while maintaining efficiency and integrating with broader marketing systems." },
  ],
  technologyCategories: [
    { title: "DSPs", icon: "Globe", items: ["The Trade Desk", "DV360", "Amazon DSP"] },
    { title: "Verification", icon: "Shield", items: ["DoubleVerify", "IAS", "MOAT"] },
    { title: "Data", icon: "Users", items: ["LiveRamp", "Lotame", "Oracle Data Cloud"] },
    { title: "Analytics", icon: "LineChart", items: ["Google Analytics 4", "Campaign Manager 360", "Looker Studio"] },
  ],
  techTitle: "Programmatic Tools",
  techDescription: "Enterprise-grade demand-side platforms and verification tools. Platform selection based on your needs.",
  qualification: {
    forYou: [
      "You require broad reach with targeting precision",
      "You have brand safety requirements and quality standards",
      "You're ready to invest in proper measurement and attribution",
      "You have or can develop diverse creative assets",
    ],
    notForYou: [
      "You prioritize volume over quality and brand safety",
      "You cannot invest in proper tracking infrastructure",
      "You expect immediate, direct-response-only results",
      "You're unwilling to develop varied creative formats",
    ],
  },
  faqs: [
    { question: "What budget level is appropriate for programmatic advertising?", answer: "Programmatic can be effective at various investment levels, but meaningful scale typically requires sufficient budget to reach your audience with appropriate frequency. We assess opportunity size during discovery and recommend investment levels based on your goals and competitive landscape." },
    { question: "How do you ensure brand safety in programmatic campaigns?", answer: "We implement multiple layers of protection including pre-bid blocking, verification partnerships, approved inventory lists, and ongoing monitoring. Brand safety protocols are established during planning and continuously refined based on campaign data." },
    { question: "How long does it take to see results from programmatic?", answer: "Programmatic often requires a learning period to optimize bidding and targeting. Initial performance insights emerge within weeks, but building mature campaigns with efficient delivery typically takes two to three months of active management." },
    { question: "How does programmatic fit with our other advertising channels?", answer: "Programmatic works best as part of an integrated media strategy. We coordinate with paid search for demand capture, paid social for audience development, and overall analytics for unified measurement and attribution." },
    { question: "What reporting will we receive?", answer: "Regular reporting covers impressions, reach, frequency, viewability, and conversion metrics. We provide strategic analysis connecting campaign performance to business outcomes, not just platform data exports." },
    { question: "Do you handle creative development for programmatic?", answer: "We provide creative specifications and performance guidance, but typically work with your internal team or creative partners for asset production. We can coordinate with creative resources to ensure assets are optimized for programmatic delivery." },
  ],
  finalCtaTitle: "Programmatic that performs.",
  finalCtaDescription: "Reach the right audiences in brand-safe environments with measurable outcomes.",
  finalCtaButtonText: "Start a serious conversation",
};

const Programmatic = () => {
  const { content } = usePageContent("programmatic", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      heroSecondaryText={content.heroSecondaryText}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      systemShift={content.systemShift}
      engagementShape={content.engagementShape}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default Programmatic;
